import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * Base API URL from environment variables.
 * In development, uses empty string to utilize the proxy configured in package.json
 * In production, uses the full backend URL
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Axios instance configured for the backend API.
 * - Includes credentials for session-based authentication
 * - Sets JSON content type
 * - Handles common error responses
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies for session-based auth
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30-second timeout
});

/**
 * Response interceptor for handling errors globally.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;

      switch (status) {
        case 401:
          // Unauthorized - only redirect for protected API calls, not auth checks
          // Don't redirect for auth endpoints or public pages
          const requestUrl = error.config?.url || '';
          const currentPath = window.location.pathname;
          const isAuthEndpoint = requestUrl.includes('/api/auth/');
          const isPublicPage = currentPath === '/' || currentPath === '/login' || currentPath === '/register';

          if (!isAuthEndpoint && !isPublicPage) {
            console.error('Unauthorized access - please login');
            window.location.href = '/login';
          }
          break;
        case 403:
          console.error('Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error(`API error: ${status}`);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server');
    } else {
      // Error setting up the request
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
